import {
	buildPartnerWorkerSenderRunPath,
	fetchPartnerWorkerBuildLogs,
	fetchPartnerWorkerBuilds,
	fetchPartnerWorkerClients,
	fetchPartnerWorkerDetail,
	fetchPartnerWorkerLandingDomains,
	fetchPartnerWorkerLandingEventStats,
	fetchPartnerWorkerLandingLinks,
	fetchPartnerWorkerLandingSites,
	fetchPartnerWorkerProfits,
	fetchPartnerWorkerSenderAuthJobs,
	fetchPartnerWorkerSenderRuns,
	fetchPartnerWorkerSenderSessions,
} from '@/api/partner-workers';
import { ROUTES } from '@/app/routes';
import EntityHeroCard from '@/components/navigation/entity-hero-card';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import type { IAdminUserDetail } from '@/entities/admin-user.entity';
import { PageScroll } from '@/layouts/page-layout';
import BuildsTab from '@/pages/admin/users/detail/builds-tab';
import ClientsTab from '@/pages/admin/users/detail/clients-tab';
import LandingsTab from '@/pages/admin/users/detail/landings-tab';
import ProfitsTab from '@/pages/admin/users/detail/profits-tab';
import SenderTab from '@/pages/admin/users/detail/sender-tab';
import UserStats from '@/pages/admin/users/detail/user-stats';
import { ArrowLeftIcon } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import TemplatesSubtab from './templates-subtab';
import WorkerTabs, { type PartnerWorkerTab } from './worker-tabs';

export default function PartnerWorkerDetailPage() {
	const params = useParams();
	const navigate = useNavigate();
	const workerProfileId = Number(params.profileId);
	const [user, setUser] = useState<IAdminUserDetail | null>(null);
	const [loading, setLoading] = useState(true);
	const [activeTab, setActiveTab] = useState<PartnerWorkerTab>('clients');

	const loadUser = useCallback(async () => {
		if (!Number.isFinite(workerProfileId)) return;
		setLoading(true);
		try {
			const data = await fetchPartnerWorkerDetail(workerProfileId);
			setUser(data);
		} finally {
			setLoading(false);
		}
	}, [workerProfileId]);

	useEffect(() => {
		void loadUser();
	}, [loadUser]);

	if (!Number.isFinite(workerProfileId)) {
		return <div className='p-4 text-muted-foreground text-sm'>Некорректный ID воркера</div>;
	}

	if (loading || !user) {
		return (
			<div className='h-full flex items-center justify-center'>
				<Spinner />
			</div>
		);
	}

	return (
		<PageScroll contentClassName='p-2 flex flex-col gap-3'>
			<EntityHeroCard
				title={`#${user.nickname}`}
				subtitle='Воркер'
				trailing={
					<Button type='button' variant='outline' size='sm' onClick={() => navigate(ROUTES.workers)}>
						<ArrowLeftIcon size={14} className='mr-1' />
						Назад
					</Button>
				}
			/>
			<UserStats user={user} />
			<WorkerTabs activeTab={activeTab} onTabChange={setActiveTab} />

			{activeTab === 'clients' && (
				<ClientsTab profileId={workerProfileId} fetchClients={fetchPartnerWorkerClients} />
			)}
			{activeTab === 'builds' && (
				<BuildsTab
					key={workerProfileId}
					profileId={workerProfileId}
					readOnly
					fetchBuilds={fetchPartnerWorkerBuilds}
					fetchBuildLogs={buildId => fetchPartnerWorkerBuildLogs(workerProfileId, buildId)}
				/>
			)}
			{activeTab === 'profits' && (
				<ProfitsTab
					key={workerProfileId}
					profileId={workerProfileId}
					nickname={user.nickname}
					readOnly
					fetchProfits={fetchPartnerWorkerProfits}
				/>
			)}
			{activeTab === 'landings' && (
				<LandingsTab
					key={workerProfileId}
					profileId={workerProfileId}
					fetchDomains={fetchPartnerWorkerLandingDomains}
					fetchSites={fetchPartnerWorkerLandingSites}
					fetchLinks={fetchPartnerWorkerLandingLinks}
					fetchStats={fetchPartnerWorkerLandingEventStats}
				/>
			)}
			{activeTab === 'templates' && <TemplatesSubtab workerProfileId={workerProfileId} />}
			{activeTab === 'sender' && (
				<SenderTab
					key={workerProfileId}
					profileId={workerProfileId}
					readOnly
					buildRunPath={buildPartnerWorkerSenderRunPath}
					fetchRuns={fetchPartnerWorkerSenderRuns}
					fetchSessions={fetchPartnerWorkerSenderSessions}
					fetchAuthJobs={fetchPartnerWorkerSenderAuthJobs}
				/>
			)}
		</PageScroll>
	);
}
